import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IMPORT_ORDER_VALIDATION_TRACING_ACTIONS } from './import-order-validation-tracing.actions';
import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { createCancelImportTrace } from '../factories/import-order-validation-factories';
import {
  IMPORT_ORDERS_ACTIONS,
  MY_ORDERS_ACTIONS,
  OrderItemDTOService,
} from '@usf/ngrx-order';
import { Tracking } from '@panamax/app-state';
import { ImportOrderValidationTracingConstants } from './import-order-validation-tracing.constants';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { selectProductAttributesByIds } from '@shared/selectors/product.selectors';
import { SharedTrackingService } from '@shared/services/analytics/shared-tracking.service';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class ImportOrderValidationTracingEffects {
  cancelImportOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.cancelClick),
      map(() => {
        const tracking = createCancelImportTrace();
        tracking.tracing.data.isStartOfTrace = true;
        tracking.tracing.data.isEndOfTrace = true;
        return OrderTracingService.deepRemoveUndefined(tracking);
      }),
      switchMap(tracking => [
        IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.cancelImportOrder({ tracking }),
      ]),
    ),
  );

  loadImportOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDERS_ACTIONS.load),
      map(event => {
        const tracking: Tracking = {
          tracing: {
            data: {
              traceContext:
                ImportOrderValidationTracingConstants.loadImportOrder,
              isEndOfTrace: true,
              isStartOfTrace: true,
              attributes: {
                order: {
                  fileFormat: event?.fileType,
                },
                event: ImportOrderValidationTracingConstants.loadImportOrder,
              },
            },
            transformFunc: genericTracingTransformer,
          },
        };
        return OrderTracingService.deepRemoveUndefined(tracking);
      }),
      switchMap(tracking => [
        IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.loadImportOrder({ tracking }),
      ]),
    ),
  );

  submitImportOrdersApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDERS_ACTIONS.submit),
      map(action => {
        const tracking: Tracking = {
          tracing: {
            data: {
              traceContext:
                ImportOrderValidationTracingConstants.submitImportOrderContext,
              isEndOfTrace: false,
              isStartOfTrace: false,
              attributes: {
                order: {
                  importedOrdersCount: action?.orders?.length,
                  importOrderId: action?.orders[0]?.importOrderIdentifier,
                },
                event:
                  ImportOrderValidationTracingConstants.submitImportOrderEvent,
              },
            },
            transformFunc: genericTracingTransformer,
          },
        };
        return OrderTracingService.deepRemoveUndefined(tracking);
      }),
      switchMap(tracking => [
        IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.traceSubmitImportOrderApi({
          tracking,
        }),
      ]),
    ),
  );

  submitImportOrdersApiResponseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDERS_ACTIONS.submitSuccess),
      map(action => {
        const tracking: Tracking = {
          tracing: {
            data: {
              traceContext:
                ImportOrderValidationTracingConstants.submitImportOrderContext,
              isEndOfTrace: false,
              isStartOfTrace: false,
              attributes: {
                order: {
                  importedOrdersCount: action?.expectation?.orderCount,
                  importOrderId: action?.expectation?.importOrderIdentifier,
                },
                event:
                  ImportOrderValidationTracingConstants.submitImportOrderEvent,
              },
            },
            transformFunc: genericTracingTransformer,
          },
        };
        return OrderTracingService.deepRemoveUndefined(tracking);
      }),
      switchMap(tracking => [
        IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.traceSubmitImportOrderApiResponseSuccess(
          {
            tracking,
          },
        ),
      ]),
    ),
  );

  submitImportOrderApiResponseFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IMPORT_ORDERS_ACTIONS.submitFail),
      map(error => {
        const tracking: Tracking = {
          tracing: {
            data: {
              traceContext:
                ImportOrderValidationTracingConstants.submitImportOrderContext,
              isEndOfTrace: true,
              isStartOfTrace: false,
              attributes: {
                order: {
                  errorDetails: !!error ? error : {},
                },
                event:
                  ImportOrderValidationTracingConstants.submitImportOrderEvent,
              },
            },
            transformFunc: genericTracingTransformer,
          },
        };
        return OrderTracingService.deepRemoveUndefined(tracking);
      }),
      switchMap(error => [
        IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.traceSubmitImportOrderApiResponseFail(
          {
            tracking: OrderTracingService.deepRemoveUndefined(error),
          },
        ),
      ]),
    ),
  );

  submitImportOrderWebsocketResponse$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(
          ofType(
            IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.traceSubmitImportOrderApiResponseSuccess,
          ),
        ),
        this.actions$.pipe(
          ofType(
            //websocket actions
            MY_ORDERS_ACTIONS.submitIpOrderAsync,
          ),
        ),
      ]).pipe(
        filter(
          ([_, myOrdersAction]) =>
            myOrdersAction?.orderTransactions?.length > 0,
        ),
        tap(submittedOrderAndNewOrderArr => {
          const [submittedOrder, webSocketOrder] = submittedOrderAndNewOrderArr;
          const websocketMsgContentLength =
            webSocketOrder?.orderTransactions.length;
          const newOrder =
            websocketMsgContentLength === 1
              ? webSocketOrder?.orderTransactions[0]
              : webSocketOrder?.orderTransactions[1]; //websocket order

          const productNumbers = newOrder?.orderItems?.map(orderItem => {
            return orderItem.productNumber;
          });
          this.store
            .select(selectProductAttributesByIds(productNumbers))
            .pipe(take(1))
            .subscribe(productMap => {
              if (
                submittedOrder?.tracking?.tracing?.data?.attributes?.order[
                  'importOrderId'
                ] === newOrder?.importOrderIdentifier
              ) {
                const webSocketOrderItems =
                  this.orderItemDtoService.toClientModelArray(
                    newOrder?.orderItems,
                  );
                const webSocketOrderProducts =
                  SharedTrackingService.tracingProductData(
                    webSocketOrderItems,
                    productMap,
                  );
                const trackingData = {
                  tracing: {
                    data: {
                      isEndOfTrace: true,
                      isStartOfTrace: false,
                      traceContext:
                        ImportOrderValidationTracingConstants.submitImportOrderContext,
                      attributes: {
                        event:
                          ImportOrderValidationTracingConstants.submitImportOrderEvent,
                        order: {
                          traceContext:
                            ImportOrderValidationTracingConstants.submitImportOrderContext,
                          orderId: newOrder?.orderId || undefined,
                          origOrderId: newOrder?.origOrderId || undefined,
                          origSplitOrderId:
                            newOrder?.origSplitOrderId || undefined,
                          tandemOrderNumber: newOrder?.tandemOrderNumber,
                          requestedDeliveryDate:
                            newOrder?.requestedDeliveryDate,
                          confirmedDeliveryDate:
                            newOrder?.confirmedDeliveryDate,
                          totalAmount: newOrder?.totalDollars,
                          openMethod:
                            ImportOrderValidationTracingConstants.submitImportOrderOpenMethod,
                        },
                        webSocketOrderProducts: webSocketOrderProducts,
                        submittedOrderProducts: [],
                        errorDetails: newOrder.errorDetails || undefined,
                      },
                    },
                  },
                } as Tracking;
                this.orderTracingService.trackSubmitImportOrderWebsocket(
                  trackingData,
                );
              }
            });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private orderItemDtoService: OrderItemDTOService,
    private orderTracingService: OrderTracingService,
    private store: Store<any>,
  ) {}
}
